import React from 'react';
import { Link } from 'react-router-dom';
import { Eye, Target, GraduationCap, Briefcase, Lightbulb, ArrowRight } from 'lucide-react';
import ShopNavbar from '../components/ShopNavbar';
import ShopFooter from '../components/ShopFooter';
import '../styles/shop.css';

const MISSION = [
  'Make well-made clothing affordable for every Indian, from a ₹399 tee to a ₹15,999 hand-stitched Banarasi.',
  'Bring luxury-grade fabric, finishing and craftsmanship to every price, not only to the top end.',
  'Tell India\'s stories, from Mughal gardens and Gupta coins to Bengal\'s kantha and the freedom movement, through design.',
  'Work directly with weavers and artisans so that more of the price reaches the hands that make it.',
  'Be honest about price: GST-inclusive prices, no platform fee, no processing fee, no hidden charges.',
];

const Card = ({ icon: Icon, title, color, children, testid }) => (
  <section className="si-card p-6 md:p-8" data-testid={testid}>
    <div className="flex items-center gap-3 mb-4">
      <span className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: `${color}1A` }}><Icon className="w-5 h-5" style={{ color }} /></span>
      <h2 className="text-2xl font-bold">{title}</h2>
    </div>
    {children}
  </section>
);

const Intro = () => (
  <section className="si-card p-6 md:p-10" data-testid="story-intro">
    <p className="text-sm font-bold text-[var(--si-orange)] uppercase mb-2">Our Story</p>
    <h1 className="text-4xl sm:text-5xl font-bold leading-tight">Every Design Has a Story for You.</h1>
    <p className="mt-4 text-lg text-[var(--si-grey)] max-w-3xl">Scholar Industry is an Indian clothing brand built on one simple belief: great quality should not depend on how much you can spend.</p>
    <div className="mt-6 grid md:grid-cols-2 gap-4 text-[15px] leading-relaxed">
      <p><strong>Scholar</strong> stands for curiosity: the research, reading and close look at India's history, art and craft that goes into every design.</p>
      <p><strong>Industry</strong> stands for making: fabric, cut, stitch and finish that turn those ideas into clothing you can wear every day.</p>
    </div>
  </section>
);

const Founder = () => (
  <section className="si-card overflow-hidden" data-testid="story-founder">
    <div className="bg-[var(--si-navy)] text-white p-6 md:p-10">
      <p className="text-sm font-bold text-[var(--si-yellow)] uppercase">About the Founder</p>
      <h2 className="mt-2 text-2xl md:text-3xl font-bold leading-tight max-w-3xl">An engineer who asked why good clothing had to cost so much, and decided to fix it.</h2>
      <div className="mt-6 flex flex-wrap gap-3 text-sm font-semibold">
        <span className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2"><GraduationCap className="w-4 h-4 text-[var(--si-yellow)]" /> M.Tech, IIT Jodhpur</span>
        <span className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2"><Briefcase className="w-4 h-4 text-[var(--si-yellow)]" /> 6 years in top MNCs</span>
        <span className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2"><Lightbulb className="w-4 h-4 text-[var(--si-yellow)]" /> Young, passionate entrepreneur</span>
      </div>
    </div>
    <div className="p-6 md:p-10 space-y-4 text-[15px] md:text-base leading-relaxed">
      <p>Our founder is a young, passionate entrepreneur with an <strong>M.Tech from IIT Jodhpur</strong> and <strong>six years of experience in leading multinational companies</strong>. Years of solving complex problems in the corporate world taught him to look past the label and ask a simple question: <em>what is the real value of this?</em></p>
      <p><strong>The problem he faced.</strong> Like most of us, he found the clothing market split in two. Affordable clothes faded, shrank or lost their shape after a few washes. Clothes made with real quality and real craft came with luxury price tags, inflated by middlemen, branding and hidden charges. He saw the same thing in heritage wear: weavers and artisans who create India's finest sarees, kurtas and punjabis earned very little, while buyers paid many times more. Ordinary families had to choose between price and quality, and India's beautiful stories of coins, empires, temples and freedom were missing from everyday fashion.</p>
      <p><strong>The solution he built.</strong> Instead of accepting it, he founded <strong>Scholar Industry</strong>. He applied the same engineering discipline to clothing: fabric-tested, shrink-tested and wash-tested, with costs worked out line by line, and buying directly from mills and artisan clusters. The goal is simple: <strong>the best quality at the lowest possible price, in every segment from affordable to luxury</strong>. That is why a ₹399 Gen-Z tee is made with the same care as a ₹15,999 hand-stitched Banarasi with original coin buttons. It is also why prices include GST, with no platform or processing fee.</p>
      <p>Today he personally oversees every collection, from the first sketch inspired by a museum coin or a temple carving to the final stitch, so that every design truly has a story for you.</p>
      <blockquote className="border-l-4 border-[var(--si-orange)] pl-4 py-1 text-lg font-semibold text-[var(--si-navy)]" data-testid="founder-quote">
        "Luxury should be about quality and story, not about the price tag. I want every Indian to wear clothing that lasts, and that means something."
        <span className="block text-sm font-medium text-[var(--si-grey)] mt-1">Founder, Scholar Industry</span>
      </blockquote>
    </div>
  </section>
);

export const OurStory = () => (
  <div className="si-page">
    <ShopNavbar />
    <main className="max-w-5xl mx-auto px-3 md:px-6 py-4 md:py-6 space-y-4" data-testid="our-story-page">
      <Intro />
      <div className="grid md:grid-cols-2 gap-4">
        <Card icon={Eye} title="Our Vision" color="#1F4FB8" testid="story-vision">
          <p className="text-lg font-semibold leading-snug">To become India's most loved storytelling clothing brand, where luxury-grade quality is available at every price, and every garment carries a piece of India's heritage.</p>
          <p className="mt-3 text-[15px] text-[var(--si-grey)]">We imagine a country where a student, a professional and a bride all wear clothing that is honestly priced, beautifully made and rich with meaning.</p>
        </Card>
        <Card icon={Target} title="Our Mission" color="#FB641B" testid="story-mission">
          <ul className="space-y-2.5">
            {MISSION.map((m) => (
              <li key={m} className="flex gap-2.5 text-[15px] leading-snug"><span className="mt-1.5 w-2 h-2 rounded-full bg-[var(--si-orange)] shrink-0" />{m}</li>
            ))}
          </ul>
        </Card>
      </div>
      <Founder />
      <div className="si-card p-6 text-center">
        <p className="text-lg font-bold">Find the design that tells your story.</p>
        <Link to="/shop" className="mt-3 si-btn si-btn-buy" data-testid="story-shop-cta">Shop the Collection <ArrowRight className="w-4 h-4" /></Link>
      </div>
    </main>
    <ShopFooter />
  </div>
);

export default OurStory;
